import { StatusData } from "@fairfleet/geotab";
import { DiagnosticNaming, MotorTheme, THEME_ORDER, inferTheme } from "./motorTheme";

/** One measurement of a vehicle over the period, summarised. */
export interface DiagnosticSummary {
  diagnosticId: string;
  name: string;
  theme: MotorTheme;
  /** Where the reading came from — the engine bus, the telematics unit, an add-on. */
  sourceId: string | null;
  sourceName: string | null;
  unit: string;
  /** How many readings arrived in the period. */
  count: number;
  /** The last reading, as it came, so a non-numeric one is not lost. */
  latest: unknown;
  latestAt: Date;
  /** `null` where no reading was a number: a state or a text reading has no minimum. */
  min: number | null;
  max: number | null;
  average: number | null;
}

/**
 * What the summary needs to know about the diagnostics, keyed by id.
 *
 * `unitOfMeasure` is an entity with a name, not a string — reading it as one puts "[object
 * Object]" in the unit column.
 */
export type DiagnosticLookup = Record<
  string,
  (DiagnosticNaming & { unitOfMeasure?: { name?: string } }) | undefined
>;

/**
 * Summarises a period of readings, one entry per diagnostic.
 *
 * Sorted by group and then by name, so the table reads the same way twice in a row: the raw
 * readings arrive in whatever order Geotab returns them.
 *
 * @param statusData - Every reading in the period, in any order.
 * @param diagnostics - What is known about the diagnostics.
 * @returns One summary per diagnostic that reported.
 */
export function summariseDiagnostics(
  statusData: StatusData[],
  diagnostics: DiagnosticLookup,
): DiagnosticSummary[] {
  const byDiagnostic = new Map<string, StatusData[]>();

  for (const entry of statusData) {
    const diagnosticId = entry.diagnostic?.id;
    if (!diagnosticId) {
      continue;
    }
    const existing = byDiagnostic.get(diagnosticId);
    if (existing) existing.push(entry);
    else byDiagnostic.set(diagnosticId, [entry]);
  }

  const summaries: DiagnosticSummary[] = [];

  for (const [diagnosticId, entries] of byDiagnostic) {
    const sorted = [...entries].sort((a, b) => time(a) - time(b));
    const newest = sorted[sorted.length - 1];
    const numbers = numericStatistics(sorted);
    const diagnostic = diagnostics[diagnosticId];

    summaries.push({
      diagnosticId,
      name: diagnostic?.name ?? diagnosticId,
      theme: inferTheme(diagnosticId, diagnostic),
      sourceId: diagnostic?.source?.id ?? null,
      sourceName: diagnostic?.source?.name ?? null,
      unit: diagnostic?.unitOfMeasure?.name ?? "",
      count: sorted.length,
      latest: newest.data,
      latestAt: new Date(newest.dateTime),
      min: numbers.min,
      max: numbers.max,
      average: numbers.average,
    });
  }

  return summaries.sort(byThemeThenName);
}

function byThemeThenName(first: DiagnosticSummary, second: DiagnosticSummary) {
  const byTheme = THEME_ORDER.indexOf(first.theme) - THEME_ORDER.indexOf(second.theme);
  return byTheme !== 0 ? byTheme : first.name.localeCompare(second.name);
}

/**
 * Smallest, largest and mean of the readings that are numbers, in one pass.
 *
 * Not `Math.min(...values)`: that passes every value as an argument, and a busy vehicle produces
 * enough readings in a day to exceed the call stack limit and throw `RangeError` — which took the
 * page down rather than showing a figure.
 */
function numericStatistics(entries: StatusData[]) {
  let min: number | null = null;
  let max: number | null = null;
  let total = 0;
  let count = 0;

  for (const entry of entries) {
    const value = Number(entry.data);
    if (!Number.isFinite(value)) {
      continue;
    }
    if (min === null || value < min) min = value;
    if (max === null || value > max) max = value;
    total += value;
    count++;
  }

  return { min, max, average: count > 0 ? total / count : null };
}

function time(entry: StatusData) {
  return new Date(entry.dateTime).getTime();
}
